/* eslint react-hooks/immutability: ["warn"] */
import { useCallback, useMemo, useState } from "react";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useTranslation } from "@coworker/locales";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { Media } from "../types/media";
import { MediaTypes } from "../constants";
import { MediaSettingsForm } from "../Organisms/MediaSettingsForm";
import { FullWidthButton } from "../../SkapaButton";
import { useCreateMedia } from "../hooks/useCreateMedia";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../hooks/queryKeys";
import { useAddImageToMedia } from "../hooks/useAddImageToMedia";
import { useMediasByStoreId } from "../hooks/useMediasByStoreId";
import { useStoreId } from "../../../core/auth/useLoggedInUser";
import {
  checkEmptyMediaName,
  checkMediaName,
} from "../Organisms/Settings/SettingsForm.helper";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { getMediaTypeText, getRequiredFields } from "../tools";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import { useWriteSignedUrls } from "../hooks/useWriteSignedUrls";
import { uploadToImage } from "../Helpers/imageHelpers";

export function CreateMediaView() {
  const createMediaMutation = useCreateMedia();
  const storeId = useStoreId();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useFixaNavigate();

  const [images, setImages] = useState<NewOrExistingFile[]>([]);
  const [currentMedia, setCurrentMedia] = useState<Media>({
    mediaType: MediaTypes.NONE,
    storeId: storeId,
    active: "INACTIVE",
  } as Media);
  const [currentCheckedMediaType, setCurrentCheckedMediaType] =
    useState<MediaTypes>(MediaTypes.NONE);
  const [mediaIsUpdated, setMediaIsUpdated] = useState(false);
  const { data: mediasResponse } = useMediasByStoreId(storeId);
  const addImageToMedia = useAddImageToMedia(currentMedia.id);

  const linkbar = (
    <Linkbar
      currentLabel={t("addMediaString")}
      items={[
        {
          label: t("mediaMaintenanceString"),
          to: "/mediamaintenance",
        },
      ]}
    />
  );

  const handleImagesChange = useCallback((newFiles: NewOrExistingFile[]) => {
    setImages(newFiles);
    setMediaIsUpdated(true);
  }, []);

  const onImageUpdate = async (_image: NewOrExistingFile) => {};

  const submitDisabled =
    !mediasResponse ||
    checkEmptyMediaName(currentMedia) ||
    checkMediaName(currentMedia, mediasResponse.medias || []) ||
    !mediaIsUpdated ||
    !validateMediaFields(currentMedia);

  const useWriteSignedUrlsFunc = useMemo(() => useWriteSignedUrls, []);
  const imageSize = useMemo(() => ({ maxWidthOrHeight: 1024 }), []);

  const {
    //ready: imageUploaderIsReady,
    upload: uploadImages,
    createUploads: imagesCreateUploads,
    //error: imageUploaderError,
  } = useImageUploader(images, imageSize, useWriteSignedUrlsFunc);

  const onSubmit = async () => {
    if (!submitDisabled) {
      try {
        await uploadImages();

        const mediaId = await createMediaMutation.mutateAsync(currentMedia);
        if (!mediaId) {
          alert(t("searchErrorString"));
          return;
        }

        for (const [index, uploads] of imagesCreateUploads.entries()) {
          const image = uploadToImage(
            uploads,
            mediaId.mediaId,
            index,
            images[index]?.rotation ?? 0
          );
          try {
            await addImageToMedia.mutateAsync(image);
          } catch {
            alert(t("searchErrorString"));
          }
        }
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ImagesByMedia],
        });
        navigate(
          `/mediamaintenance/tabs/${mediaId.mediaId}?tab=article-list-tab`
        );
      } catch {
        alert(t("searchErrorString"));
      }
    }
  };

  function validateMediaFields(media: Media): boolean {
    const requiredKeys = getRequiredFields(media);
    return requiredKeys.every((key) => {
      const value = media[key as keyof Media];
      return value;
    });
  }

  return (
    <FullScreenPopup
      noPadding
      linkbar={linkbar}
      fullHeightContent={true}
      fieldsAreEmpty={submitDisabled}
      actionBarContent={
        <FullWidthButton
          type="primary"
          text={t("createNameString", {
            name: getMediaTypeText(currentMedia.mediaType as MediaTypes),
          })}
          onClick={onSubmit}
        />
      }
    >
      <MediaSettingsForm
        currentMedia={currentMedia}
        medias={mediasResponse?.medias || []}
        setCurrentMedia={(newMedia) => {
          setMediaIsUpdated(true);
          setCurrentMedia(newMedia);
        }}
        currentCheckedMediaType={currentCheckedMediaType}
        setCurrentCheckedMediaType={setCurrentCheckedMediaType}
        images={images}
        onImagesChange={handleImagesChange}
        onImageUpdate={onImageUpdate}
        isUpdate={false}
      />
    </FullScreenPopup>
  );
}
